import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { UserProvider, useUser } from "./context/UserContext";
import Navbar from "./components/layout/Navbar";
import CompareBar from "./components/common/CompareBar";
import AuthModal from "./components/common/AuthModal";
import HomePage from "./pages/HomePage";
import ExplorePage from "./pages/ExplorePage";
import ComparePage from "./pages/ComparePage";
import FinderPage from "./pages/FinderPage";
import JobsPage from "./pages/JobsPage";
import ToolsPage from "./pages/ToolsPage";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import AdminPage from "./pages/AdminPage";
import "./styles/globals.css";
import ScrollToTop from "./ScrollToTop/ScrollToTop";
import WhatsAppButton from "./whatsapp/whatsappIcon";

export const CompareContext = React.createContext(null);

// Context so any page can trigger the auth popup
export const AuthModalContext = React.createContext(null);

// Blog-admin can only reach the Blogs section of /admin
const BLOG_ADMIN_ALLOWED_PREFIXES = ["/admin/blogs", "/admin/add-blog", "/admin/edit-blog"];

// Admin route — full admin passes through; blog-admin is confined to blog
// pages (redirected before AdminPage/AdminDashboard ever mounts, so the
// dashboard's unconditional stats fetch never fires for them); anyone else
// is redirected to /
const AdminRoute = ({ children }) => {
  const { isLoggedIn, isAdmin, isBlogAdmin } = useUser();
  const location = useLocation();
  if (!isLoggedIn) return <Navigate to="/" replace />;
  if (isAdmin) return children;
  if (isBlogAdmin) {
    const allowed = BLOG_ADMIN_ALLOWED_PREFIXES.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + "/"),
    );
    return allowed ? children : <Navigate to="/admin/blogs" replace />;
  }
  return <Navigate to="/" replace />;
};

function AppRoutes() {
  const [compareList, setCompareList] = useState([]);
  const [compareAlert, setCompareAlert] = useState(false);
  const { isLoggedIn, isAdmin } = useUser();
  const navigate = useNavigate();

  // Auth modal state
  const [authModal, setAuthModal] = useState(null); // null | "login" | "register"

  const openAuthModal = useCallback((mode = "login") => setAuthModal(mode), []);
  const closeAuthModal = useCallback(() => setAuthModal(null), []);

  const handleAuthSuccess = useCallback(
    (user) => {
      closeAuthModal();
      if (user?.role === "admin") navigate("/admin");
      else if (user?.role === "blog-admin") navigate("/admin/blogs");
    },
    [closeAuthModal, navigate],
  );

  // 60-second popup for guests — show every 60s if not logged in
  useEffect(() => {
    if (isLoggedIn) return;
    // Show immediately on first visit (after 2s delay so page loads)
    const firstTimer = setTimeout(() => {
      setAuthModal((prev) => prev ?? "login");
    }, 2000);

    // Then every 60s after dismissal
    const intervalTimer = setInterval(() => {
      setAuthModal((prev) => prev ?? "login");
    }, 60000);

    return () => {
      clearTimeout(firstTimer);
      clearInterval(intervalTimer);
    };
  }, [isLoggedIn]);

  const toggleCompare = (course) => {
    setCompareList((prev) => {
      const exists = prev.find((c) => c.id === course.id);
      if (exists) return prev.filter((c) => c.id !== course.id);
      if (prev.length >= 3) {
        setCompareAlert(true);
        setTimeout(() => setCompareAlert(false), 3000);
        return prev;
      }
      return [...prev, course];
    });
  };

  const clearCompare = () => setCompareList([]);

  return (
    <AuthModalContext.Provider value={{ openAuthModal }}>
      <CompareContext.Provider
        value={{ compareList, toggleCompare, clearCompare }}
      >
        <ScrollToTop />
        {/* Navbar always visible */}
        <Navbar />

        <Routes>
          {/* Admin — protected, only role=admin can access */}
          <Route
            path="/admin/*"
            element={
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            }
          />

          {/* All main routes are PUBLIC — auth is handled via popup */}
          <Route path="/" element={<HomePage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/finder" element={<FinderPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {isLoggedIn && (
          <>
            {compareAlert && (
              <div
                style={{
                  position: "fixed",
                  top: 20,
                  right: 20,
                  zIndex: 9999,
                  background: "#fffbeb",
                  border: "1.5px solid #f59e0b",
                  color: "#92400e",
                  borderRadius: 12,
                  padding: "12px 18px",
                  fontSize: 14,
                  fontWeight: 500,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                  animation: "slideInToast 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                ⚠️ You can compare up to 3 courses only
              </div>
            )}
            <CompareBar />
            <WhatsAppButton />
          </>
        )}

        <style>
          {
            "@keyframes slideInToast { from{opacity:0;transform:translateX(40px)} to{opacity:1;transform:translateX(0)} }"
          }
        </style>

        {/* Auth Modal Popup */}
        {authModal && (
          <AuthModal
            mode={authModal}
            onClose={closeAuthModal}
            onSuccess={handleAuthSuccess}
          />
        )}
      </CompareContext.Provider>
    </AuthModalContext.Provider>
  );
}

function App() {
  return (
    <UserProvider>
      <Router>
        <AppRoutes />
      </Router>
    </UserProvider>
  );
}

export default App;
